import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    CircularProgress,
    Paper,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Icon,
    InputAdornment,
    TextField,
    Chip,
    Collapse
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { ESCURO } from "assets/coresTema";

export default function MDAuditModal({
    open,
    onClose,
    title,
    loading = false,
    logs = [],
    historicos,
    entityLabel = "Registro",
    entityName
}) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const [searchTerm, setSearchTerm] = useState("");
    const [expandedIds, setExpandedIds] = useState({});

    const actualLogs = useMemo(() => {
        if (Array.isArray(historicos)) return historicos;
        if (Array.isArray(logs)) return logs;
        return [];
    }, [logs, historicos]);

    // Reset de estado quando o modal fecha ou reabre
    useEffect(() => {
        if (!open) {
            setSearchTerm("");
            setExpandedIds({});
        }
    }, [open]);

    const filteredLogs = useMemo(() => {
        if (!searchTerm || !searchTerm.trim()) return actualLogs;
        const term = searchTerm.toLowerCase().trim();
        return actualLogs.filter(log => {
            const dataMatch = (log.data || "").toLowerCase().includes(term);
            const userMatch = (log.usuario || "").toLowerCase().includes(term);
            const entityMatch = (
                log.usuarioAlvoNome ||
                log.perfilNome ||
                log.regraNome ||
                log.equipeNome ||
                log.entidadeNome ||
                ""
            ).toLowerCase().includes(term);

            let altMatch = false;
            if (Array.isArray(log.alteracoes)) {
                altMatch = log.alteracoes.some(a =>
                    String(a.campo || "").toLowerCase().includes(term) ||
                    String(a.de || "").toLowerCase().includes(term) ||
                    String(a.para || "").toLowerCase().includes(term)
                );
            } else if (typeof log.alteracoes === "string") {
                altMatch = log.alteracoes.toLowerCase().includes(term);
            }

            return dataMatch || userMatch || entityMatch || altMatch;
        });
    }, [logs, searchTerm]);

    // Ao pesquisar, expande automaticamente os registros filtrados
    useEffect(() => {
        if (searchTerm && searchTerm.trim()) {
            const autoExp = {};
            filteredLogs.forEach((log, idx) => {
                autoExp[log.id || idx] = true;
            });
            setExpandedIds(autoExp);
        }
    }, [searchTerm, filteredLogs]);

    const toggleExpand = (id) => {
        setExpandedIds(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const allExpanded = useMemo(() => {
        if (!filteredLogs || filteredLogs.length === 0) return false;
        return filteredLogs.every((log, idx) => !!expandedIds[log.id || idx]);
    }, [filteredLogs, expandedIds]);

    const toggleAll = () => {
        if (allExpanded) {
            setExpandedIds({});
        } else {
            const newExp = {};
            filteredLogs.forEach((log, idx) => {
                newExp[log.id || idx] = true;
            });
            setExpandedIds(newExp);
        }
    };

    const renderValueCell = (val, isPrevious) => {
        if (val == null || String(val).trim() === "" || String(val).trim() === "-") {
            return (
                <MDTypography
                    variant="caption"
                    sx={{ color: darkMode ? "rgba(255, 255, 255, 0.4)" : "#999", fontStyle: "italic" }}
                >
                    —
                </MDTypography>
            );
        }

        const valStr = String(val);

        // Se for lista separada por " | " (ex: distribuição por operador)
        if (valStr.includes(" | ")) {
            const items = valStr.split(" | ");
            return (
                <MDBox display="flex" flexDirection="column" gap={0.5}>
                    {items.map((item, idx) => (
                        <MDBox key={idx} display="flex" alignItems="flex-start" gap={0.5}>
                            <Icon sx={{ fontSize: "14px !important", color: darkMode ? "#90caf9" : "#1976d2", mt: "2px" }}>
                                chevron_right
                            </Icon>
                            <MDTypography
                                variant="caption"
                                fontWeight="regular"
                                color={darkMode ? "white" : "dark"}
                                sx={{ wordBreak: "break-word" }}
                            >
                                {item}
                            </MDTypography>
                        </MDBox>
                    ))}
                </MDBox>
            );
        }

        // Se for valor booleano ou status
        const isYes = valStr === "Sim" || valStr === "Ativo" || valStr === "Ativado";
        const isNo = valStr === "Não" || valStr === "Nao" || valStr === "Inativo" || valStr === "Desativado";

        if (isYes) {
            return (
                <Chip
                    size="small"
                    label={valStr}
                    sx={{
                        backgroundColor: darkMode ? "rgba(76, 175, 80, 0.2)" : "#e8f5e9",
                        color: darkMode ? "#81c784" : "#2e7d32",
                        fontWeight: "bold",
                        fontSize: "0.75rem",
                        height: "22px"
                    }}
                />
            );
        }

        if (isNo) {
            return (
                <Chip
                    size="small"
                    label={valStr}
                    sx={{
                        backgroundColor: darkMode ? "rgba(244, 67, 54, 0.15)" : "#ffebee",
                        color: darkMode ? "#e57373" : "#c62828",
                        fontWeight: "bold",
                        fontSize: "0.75rem",
                        height: "22px"
                    }}
                />
            );
        }

        if (isPrevious) {
            return (
                <MDTypography
                    variant="caption"
                    color="text"
                    sx={{
                        textDecoration: "line-through",
                        color: darkMode ? "rgba(255, 255, 255, 0.7)" : "#777",
                        wordBreak: "break-word"
                    }}
                >
                    {valStr}
                </MDTypography>
            );
        }

        const isRemoval = valStr.includes("EXCLUSÃO") || valStr.includes("REMOVIDO");
        const isAddition = valStr.includes("INCLUSÃO") || valStr.includes("ADICIONADO");

        return (
            <MDTypography
                variant="caption"
                fontWeight="medium"
                sx={{
                    color: isRemoval
                        ? (darkMode ? "#e57373" : "#d32f2f")
                        : isAddition
                        ? (darkMode ? "#81c784" : "#2e7d32")
                        : (darkMode ? "#ffffff" : "#333333"),
                    wordBreak: "break-word"
                }}
            >
                {valStr}
            </MDTypography>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: darkMode ? ESCURO.painel : "#ffffff",
                    borderRadius: "12px",
                    backgroundImage: "none",
                    boxShadow: darkMode ? "0 8px 32px rgba(0, 0, 0, 0.45)" : "0 8px 32px rgba(0, 0, 0, 0.12)",
                    overflow: "hidden"
                }
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderBottom: darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #f0f2f5",
                    p: 2.5,
                    backgroundColor: darkMode ? ESCURO.cartao : "#fafafa",
                }}
            >
                <MDBox display="flex" alignItems="center" gap={1.5}>
                    <Icon sx={{ color: darkMode ? "#90caf9" : "#1976d2", fontSize: "28px !important" }}>history</Icon>
                    <MDBox>
                        <MDTypography variant="h6" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                            {title || "Histórico de Auditoria"}
                        </MDTypography>
                        <MDTypography variant="caption" color={darkMode ? "white" : "text"} opacity={0.8}>
                            Total de registros: {actualLogs.length}
                        </MDTypography>
                    </MDBox>
                </MDBox>
                <IconButton onClick={onClose} size="small" sx={{ color: darkMode ? "#fff" : "#555" }}>
                    <Icon>close</Icon>
                </IconButton>
            </DialogTitle>

            <DialogContent
                dividers
                sx={{
                    borderColor: darkMode ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)",
                    backgroundColor: darkMode ? ESCURO.painelEscuro : "#f4f6f8",
                    maxHeight: "72vh",
                    overflowY: "auto",
                    p: 2.5
                }}
            >
                {/* Barra de Busca rápida + Ação Expandir/Recolher Todos */}
                {actualLogs.length > 0 && (
                    <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2.5}>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Buscar por usuário, campo, valor anterior ou novo..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Icon fontSize="small" sx={{ color: darkMode ? "rgba(255, 255, 255, 0.7)" : "text.secondary" }}>
                                            search
                                        </Icon>
                                    </InputAdornment>
                                ),
                                endAdornment: searchTerm ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearchTerm("")} sx={{ color: darkMode ? "#fff" : "inherit" }}>
                                            <Icon fontSize="small">clear</Icon>
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                                sx: {
                                    backgroundColor: darkMode ? ESCURO.painel : "#ffffff",
                                    color: darkMode ? "#fff" : "#333",
                                    borderRadius: "8px",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: darkMode ? "rgba(255, 255, 255, 0.2)" : "#d2d6da",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                        borderColor: darkMode ? "rgba(255, 255, 255, 0.4)" : "#49a3f1",
                                    },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: darkMode ? "#49a3f1" : "#1A73E8",
                                    }
                                }
                            }}
                        />
                        {filteredLogs.length > 0 && (
                            <MDButton
                                variant="text"
                                size="small"
                                color="info"
                                onClick={toggleAll}
                                sx={{
                                    whiteSpace: "nowrap",
                                    fontSize: "0.75rem",
                                    textTransform: "none",
                                    color: darkMode ? "#90caf9 !important" : "#1976d2 !important",
                                    flexShrink: 0
                                }}
                            >
                                <Icon sx={{ mr: 0.5, fontSize: "18px !important" }}>
                                    {allExpanded ? "unfold_less" : "unfold_more"}
                                </Icon>
                                {allExpanded ? "Recolher todos" : "Expandir todos"}
                            </MDButton>
                        )}
                    </MDBox>
                )}

                {loading ? (
                    <MDBox display="flex" justifyContent="center" alignItems="center" py={6}>
                        <CircularProgress size={32} />
                        <MDTypography variant="button" ml={2} color={darkMode ? "white" : "text"}>
                            Carregando histórico de auditoria...
                        </MDTypography>
                    </MDBox>
                ) : filteredLogs.length === 0 ? (
                    <MDBox textAlign="center" py={6}>
                        <Icon sx={{ fontSize: "44px !important", color: darkMode ? "rgba(255, 255, 255, 0.5)" : "text.secondary", mb: 1 }}>
                            {searchTerm ? "search_off" : "history_toggle_off"}
                        </Icon>
                        <MDTypography variant="body2" color={darkMode ? "white" : "text"}>
                            {searchTerm
                                ? "Nenhum registro encontrado para o filtro pesquisado."
                                : "Nenhum registro de auditoria encontrado."}
                        </MDTypography>
                    </MDBox>
                ) : (
                    filteredLogs.map((log, index) => {
                        const logKey = log.id || index;
                        const isExpanded = !!expandedIds[logKey];
                        const targetName =
                            log.usuarioAlvoNome ||
                            log.perfilNome ||
                            log.regraNome ||
                            log.equipeNome ||
                            log.entidadeNome;

                        const numAlteracoes = Array.isArray(log.alteracoes) ? log.alteracoes.length : 0;

                        return (
                            <Paper
                                key={logKey}
                                elevation={1}
                                sx={{
                                    mb: 1.5,
                                    backgroundColor: darkMode ? ESCURO.painel : "#ffffff",
                                    border: darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #e0e0e0",
                                    borderRadius: "8px",
                                    overflow: "hidden",
                                    transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                                    "&:hover": {
                                        borderColor: darkMode ? "rgba(255, 255, 255, 0.25)" : "#b0bec5",
                                        boxShadow: darkMode ? "0 4px 12px rgba(0, 0, 0, 0.3)" : "0 3px 10px rgba(0, 0, 0, 0.08)"
                                    }
                                }}
                            >
                                {/* Linha Única Clicável para Estender/Recolher */}
                                <MDBox
                                    onClick={() => toggleExpand(logKey)}
                                    sx={{
                                        py: 1.2,
                                        px: 2,
                                        cursor: "pointer",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        flexWrap: "nowrap",
                                        gap: 1.5,
                                        userSelect: "none",
                                        backgroundColor: isExpanded
                                            ? (darkMode ? ESCURO.campo : "#f8f9fa")
                                            : "transparent",
                                        borderBottom: isExpanded
                                            ? (darkMode ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid #e0e0e0")
                                            : "none",
                                        transition: "background-color 0.2s ease",
                                        "&:hover": {
                                            backgroundColor: isExpanded
                                                ? (darkMode ? ESCURO.campo : "#f1f3f5")
                                                : (darkMode ? "rgba(255, 255, 255, 0.04)" : "#f9fbfd")
                                        }
                                    }}
                                >
                                    {/* Lado Esquerdo: Chevron + Data + Alvo + Contador de Alterações */}
                                    <MDBox display="flex" alignItems="center" gap={1.2} sx={{ minWidth: 0, flex: 1 }}>
                                        <Icon
                                            sx={{
                                                fontSize: "22px !important",
                                                color: darkMode ? "#90caf9" : "#1976d2",
                                                transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                                                transition: "transform 0.2s ease"
                                            }}
                                        >
                                            chevron_right
                                        </Icon>

                                        <Icon fontSize="small" sx={{ color: darkMode ? "#90caf9" : "#1976d2" }}>access_time</Icon>
                                        <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"} sx={{ fontSize: "0.85rem", whiteSpace: "nowrap" }}>
                                            {log.data || "-"}
                                        </MDTypography>

                                        {targetName && (
                                            <Chip
                                                size="small"
                                                label={targetName}
                                                sx={{
                                                    backgroundColor: darkMode ? ESCURO.painelEscuro : "#e9ecef",
                                                    color: darkMode ? "#fff" : "#333",
                                                    fontWeight: "bold",
                                                    fontSize: "0.75rem",
                                                    height: "24px",
                                                    border: darkMode ? "1px solid rgba(255, 255, 255, 0.15)" : "none"
                                                }}
                                            />
                                        )}

                                        {numAlteracoes > 0 && (
                                            <Chip
                                                size="small"
                                                label={`${numAlteracoes} ${numAlteracoes === 1 ? 'campo' : 'campos'}`}
                                                sx={{
                                                    backgroundColor: darkMode ? "rgba(255, 255, 255, 0.08)" : "#eef2f6",
                                                    color: darkMode ? "rgba(255, 255, 255, 0.75)" : "#555",
                                                    fontWeight: "medium",
                                                    fontSize: "0.7rem",
                                                    height: "20px"
                                                }}
                                            />
                                        )}
                                    </MDBox>

                                    {/* Lado Direito: Usuário */}
                                    <MDBox display="flex" alignItems="center" gap={0.5} sx={{ flexShrink: 0 }}>
                                        <Icon fontSize="small" sx={{ color: darkMode ? "#bbb" : "text.secondary" }}>person</Icon>
                                        <MDTypography variant="caption" color={darkMode ? "white" : "text"}>
                                            Por: <strong>{log.usuario || "Sistema"}</strong>
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>

                                {/* Conteúdo Estendido (Tabela Completa de Alterações) */}
                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                    <MDBox p={2} sx={{ backgroundColor: darkMode ? ESCURO.painelEscuro : "#fafbfc" }}>
                                        {Array.isArray(log.alteracoes) ? (
                                            <TableContainer
                                                component={Paper}
                                                variant="outlined"
                                                sx={{
                                                    background: darkMode ? ESCURO.painelEscuro : "#ffffff",
                                                    borderColor: darkMode ? "rgba(255, 255, 255, 0.1)" : "#e0e0e0",
                                                    borderRadius: "8px",
                                                    overflow: "hidden",
                                                    boxShadow: "none"
                                                }}
                                            >
                                                <Table size="small" sx={{ width: "100%", tableLayout: "fixed" }}>
                                                    <TableHead sx={{ display: "table-header-group", p: 0, backgroundColor: darkMode ? ESCURO.painelEscuro : "#f0f2f5" }}>
                                                        <TableRow sx={{ display: "table-row" }}>
                                                            <TableCell
                                                                component="th"
                                                                sx={{
                                                                    display: "table-cell",
                                                                    width: "30%",
                                                                    py: 1.2,
                                                                    px: 2,
                                                                    color: darkMode ? "#ffffff" : "text.secondary",
                                                                    opacity: 0.85,
                                                                    fontSize: "0.75rem",
                                                                    fontWeight: "bold",
                                                                    textTransform: "uppercase",
                                                                    whiteSpace: "nowrap",
                                                                    borderBottom: darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #e0e0e0"
                                                                }}
                                                            >
                                                                Campo / Configuração
                                                            </TableCell>
                                                            <TableCell
                                                                component="th"
                                                                sx={{
                                                                    display: "table-cell",
                                                                    width: "28%",
                                                                    py: 1.2,
                                                                    px: 2,
                                                                    color: darkMode ? "#ffffff" : "text.secondary",
                                                                    opacity: 0.85,
                                                                    fontSize: "0.75rem",
                                                                    fontWeight: "bold",
                                                                    textTransform: "uppercase",
                                                                    whiteSpace: "nowrap",
                                                                    borderBottom: darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #e0e0e0"
                                                                }}
                                                            >
                                                                Valor Anterior
                                                            </TableCell>
                                                            <TableCell
                                                                component="th"
                                                                sx={{
                                                                    display: "table-cell",
                                                                    width: "42%",
                                                                    py: 1.2,
                                                                    px: 2,
                                                                    color: darkMode ? "#ffffff" : "text.secondary",
                                                                    opacity: 0.85,
                                                                    fontSize: "0.75rem",
                                                                    fontWeight: "bold",
                                                                    textTransform: "uppercase",
                                                                    whiteSpace: "nowrap",
                                                                    borderBottom: darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #e0e0e0"
                                                                }}
                                                            >
                                                                Novo Valor
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody sx={{ display: "table-row-group" }}>
                                                        {log.alteracoes.map((alt, aIdx) => (
                                                            <TableRow
                                                                key={aIdx}
                                                                sx={{
                                                                    display: "table-row",
                                                                    backgroundColor: aIdx % 2 === 1 ? (darkMode ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 0, 0, 0.015)") : "transparent",
                                                                    "&:hover": {
                                                                        backgroundColor: darkMode ? "rgba(255, 255, 255, 0.04)" : "rgba(0, 0, 0, 0.03)"
                                                                    },
                                                                    "&:last-child td": {
                                                                        borderBottom: 0
                                                                    }
                                                                }}
                                                            >
                                                                <TableCell
                                                                    component="td"
                                                                    sx={{
                                                                        display: "table-cell",
                                                                        verticalAlign: "top",
                                                                        pt: 1.5,
                                                                        pb: 1.5,
                                                                        px: 2,
                                                                        borderBottom: darkMode ? "1px solid rgba(255, 255, 255, 0.06)" : "1px solid #f0f2f5"
                                                                    }}
                                                                >
                                                                    <MDTypography variant="caption" fontWeight="medium" color={darkMode ? "white" : "dark"}>
                                                                        {alt.campo || "-"}
                                                                    </MDTypography>
                                                                </TableCell>
                                                                <TableCell
                                                                    component="td"
                                                                    sx={{
                                                                        display: "table-cell",
                                                                        verticalAlign: "top",
                                                                        pt: 1.5,
                                                                        pb: 1.5,
                                                                        px: 2,
                                                                        borderBottom: darkMode ? "1px solid rgba(255, 255, 255, 0.06)" : "1px solid #f0f2f5"
                                                                    }}
                                                                >
                                                                    {renderValueCell(alt.de, true)}
                                                                </TableCell>
                                                                <TableCell
                                                                    component="td"
                                                                    sx={{
                                                                        display: "table-cell",
                                                                        verticalAlign: "top",
                                                                        pt: 1.5,
                                                                        pb: 1.5,
                                                                        px: 2,
                                                                        borderBottom: darkMode ? "1px solid rgba(255, 255, 255, 0.06)" : "1px solid #f0f2f5"
                                                                    }}
                                                                >
                                                                    {renderValueCell(alt.para, false)}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        ) : (
                                            <MDTypography variant="caption" color={darkMode ? "white" : "text"}>
                                                {String(log.alteracoes || "Alteração registrada")}
                                            </MDTypography>
                                        )}
                                    </MDBox>
                                </Collapse>
                            </Paper>
                        );
                    })
                )}
            </DialogContent>
            <DialogActions
                sx={{
                    p: 2,
                    borderTop: darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #f0f2f5",
                    backgroundColor: darkMode ? ESCURO.cartao : "#fafafa",
                    justifyContent: "flex-end"
                }}
            >
                <MDButton
                    variant="outlined"
                    color="secondary"
                    onClick={onClose}
                    sx={{
                        color: darkMode ? "#ffffff !important" : undefined,
                        borderColor: darkMode ? "rgba(255, 255, 255, 0.4) !important" : undefined,
                        "&:hover": {
                            borderColor: darkMode ? "#ffffff !important" : undefined
                        }
                    }}
                >
                    Fechar
                </MDButton>
            </DialogActions>
        </Dialog>
    );
}

MDAuditModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.string,
    loading: PropTypes.bool,
    logs: PropTypes.array,
    entityLabel: PropTypes.string
};
